// Which key a piece of audio sits in: a chroma (energy per pitch class) read
// off the spectrum, then the same Krumhansl–Kessler fit the MIDI estimate
// uses (key-profiles.ts), so a loop and the idea played over it suggest the
// same `key:` tag. Rough by nature — a drum loop has no key, a chord stab
// barely one — so the fit's confidence is asked for more than MIDI's, and
// the caller leaves drums out.

import { fftOf, hannWindow } from "@web-shared/lib/stretch/fft";
import { bestKey, type KeyEstimate } from "./key-profiles";

const FRAME = 4096;
/** Pitches below this are mostly rumble and the kick's body; above it,
 * harmonics that smear the chroma. The range of a bass and a lead. */
const LOW_HZ = 55;
const HIGH_HZ = 2200;
/** How much audio is read: the key is clear in the first half minute. */
const MAX_SECONDS = 30;
/** A chroma read off a spectrum is blurrier than one from notes, so the
 * flatness check above does the gatekeeping and the fit may be narrower. */
const MIN_CONFIDENCE = 0.03;

/** Energy per pitch class (C first), summed over the louder frames; all
 * zeros when there is nothing tonal to read. */
export function chromaOf(mono: Float32Array, sampleRate: number): number[] {
  const chroma = new Array<number>(12).fill(0);
  const n = Math.min(mono.length, Math.round(MAX_SECONDS * sampleRate));
  if (!(sampleRate > 0) || n < FRAME) return chroma;
  let peak = 0;
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(mono[i]!));
  if (peak === 0) return chroma;
  const fft = fftOf(FRAME);
  const window = hannWindow(FRAME);
  const hop = FRAME >> 1;
  const binHz = sampleRate / FRAME;
  const lowBin = Math.max(1, Math.ceil(LOW_HZ / binHz));
  const highBin = Math.min(FRAME >> 1, Math.floor(HIGH_HZ / binHz));
  // Each bin's pitch class, once: 12 * log2(f / C0) rounded, mod 12.
  const classOf = new Int8Array(highBin + 1);
  for (let k = lowBin; k <= highBin; k++) classOf[k] = ((Math.round(12 * Math.log2((k * binHz) / 16.3516)) % 12) + 12) % 12;
  const re = new Float64Array(FRAME);
  const im = new Float64Array(FRAME);
  const floor = (peak * peak) / 2500;
  for (let start = 0; start + FRAME <= n; start += hop) {
    let energy = 0;
    for (let i = 0; i < FRAME; i++) {
      const v = mono[start + i]! * window[i]!;
      re[i] = v;
      im[i] = 0;
      energy += v * v;
    }
    if (energy / FRAME < floor) continue;
    fft.forward(re, im);
    // Magnitude, not power: a loud fundamental would otherwise drown the
    // thirds and fifths that tell major from minor.
    for (let k = lowBin; k <= highBin; k++) chroma[classOf[k]!]! += Math.sqrt(re[k]! * re[k]! + im[k]! * im[k]!);
  }
  return chroma;
}

/** The key the audio sits in, or null when it has none to speak of. */
export function keyOfAudio(mono: Float32Array, sampleRate: number): KeyEstimate | null {
  const chroma = chromaOf(mono, sampleRate);
  const total = chroma.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return null;
  // A flat chroma (noise, drums) says nothing: the top three classes should
  // carry a clear majority of the energy before a key is read off it.
  const sorted = [...chroma].sort((a, b) => b - a);
  if ((sorted[0]! + sorted[1]! + sorted[2]!) / total < 0.4) return null;
  return bestKey(chroma, MIN_CONFIDENCE);
}
